import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight, Mail, KeyRound, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { API } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupKey, setSetupKey] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (res.data.token) {
        localStorage.setItem("admin_token", res.data.token);
      }
      navigate("/admin/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!email || !password || !setupKey) { toast.error("Please fill in all fields including setup key"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSetupLoading(true);
    let lastError = "";
    try {
      const res = await axios.post(`${API}/auth/setup`, { email, password, setup_key: setupKey }, { withCredentials: true });
      if (res.data.token) {
        localStorage.setItem("admin_token", res.data.token);
        toast.success("Admin account ready!");
        navigate("/admin/dashboard");
        return;
      }
    } catch (err1) {
      lastError = `Setup: ${err1.response?.status} - ${JSON.stringify(err1.response?.data?.detail || err1.message)}`;
      try {
        const res2 = await axios.post(`${API}/auth/magic-login`, { email, password, setup_key: setupKey }, { withCredentials: true });
        if (res2.data.token) {
          localStorage.setItem("admin_token", res2.data.token);
          toast.success("Logged in!");
          navigate("/admin/dashboard");
          return;
        }
      } catch (err2) {
        lastError += ` | Magic: ${err2.response?.status} - ${JSON.stringify(err2.response?.data?.detail || err2.message)}`;
      }
    }
    toast.error(lastError || "Unknown error");
    setSetupLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) { toast.error("Please enter your email"); return; }
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
        toast.success("Reset link generated!");
      } else {
        toast.info("If an account with that email exists, a reset link has been generated.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-3xl tracking-tighter uppercase text-white">Admin Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-2">FYB Analytics & Data</p>
        </div>

        {/* LOGIN */}
        {!showForgot && !resetToken && !showSetup && (
          <>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="Admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="admin-email-input"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="admin-password-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                data-testid="admin-login-button"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            <div className="space-y-2">
              <button
                onClick={() => { setShowForgot(true); setForgotEmail(email); }}
                className="block w-full text-center text-zinc-500 text-xs hover:text-[#D4AF37] transition-colors"
                data-testid="forgot-password-link"
              >
                <KeyRound className="w-3 h-3 inline mr-1" /> Forgot your password?
              </button>
              <button
                onClick={() => setShowSetup(true)}
                className="block w-full text-center text-zinc-600 text-xs hover:text-[#D4AF37] transition-colors"
                data-testid="show-setup-link"
              >
                <UserPlus className="w-3 h-3 inline mr-1" /> Create or reset admin account
              </button>
            </div>
          </>
        )}

        {/* SETUP FORM */}
        {showSetup && !showForgot && !resetToken && (
          <>
            <form onSubmit={handleSetup} className="space-y-4" data-testid="setup-form">
              <p className="text-zinc-400 text-sm text-center">Create a new admin account or reset an existing one.</p>
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="setup-email-input"
              />
              <Input
                type="password"
                placeholder="Set your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="setup-password-input"
              />
              <Input
                type="password"
                placeholder="Setup key"
                value={setupKey}
                onChange={(e) => setSetupKey(e.target.value)}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="setup-key-input"
              />
              <Button
                type="submit"
                disabled={setupLoading}
                className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                data-testid="setup-submit-button"
              >
                {setupLoading ? "Setting up..." : "Create / Reset Account"}
              </Button>
            </form>
            <button
              onClick={() => setShowSetup(false)}
              className="block w-full text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors"
            >
              Back to login
            </button>
          </>
        )}

        {/* FORGOT PASSWORD */}
        {showForgot && !resetToken && (
          <div className="space-y-4" data-testid="forgot-password-form">
            <p className="text-zinc-400 text-sm text-center">Enter your admin email to generate a password reset link.</p>
            <Input
              type="email"
              placeholder="Your admin email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="forgot-email-input"
            />
            <Button
              onClick={handleForgotPassword}
              disabled={forgotLoading}
              className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
              data-testid="forgot-submit-button"
            >
              {forgotLoading ? "Generating..." : "Generate Reset Link"}
            </Button>
            <button
              onClick={() => setShowForgot(false)}
              className="block w-full text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors"
            >
              Back to login
            </button>
          </div>
        )}

        {/* RESET TOKEN GENERATED */}
        {resetToken && (
          <div className="space-y-6 text-center" data-testid="reset-link-generated">
            <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-white text-sm font-medium mb-2">Reset Link Generated</p>
              <p className="text-zinc-500 text-xs mb-4">Click the button below to reset your password. This link expires in 1 hour.</p>
            </div>
            <Link
              to={`/admin/reset-password?token=${resetToken}`}
              className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
              data-testid="reset-password-link"
            >
              Reset My Password <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => { setResetToken(null); setShowForgot(false); }}
              className="block w-full text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors mt-4"
            >
              Back to login
            </button>
          </div>
        )}

        <a href="/" className="block text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
          <ArrowRight className="w-3 h-3 inline rotate-180 mr-1" /> Back to site
        </a>
      </div>
    </div>
  );
}
